import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { LuX, LuSend } from 'react-icons/lu';

import AleMessageList from './AleMessageList';
import { useAleChat } from './hooks/useAleChat';
import { ALE_CONFIG, ALE_TEXTS } from './config';

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Panel = styled.div`
  position: fixed;
  right: 20px;
  bottom: 88px;
  z-index: 90;
  width: 380px;
  max-width: calc(100vw - 40px);
  height: 560px;
  max-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.colors.white};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: ${(p) => p.theme.shadows.xl};
  animation: ${slideUp} 0.2s ease;
  transition: bottom 0.2s ease;

  body.has-price-summary-bar & {
    bottom: 180px;
    max-height: calc(100vh - 190px);
  }

  @media (max-width: 768px) {
    right: 0;
    left: 0;
    bottom: 0;
    /* No mobile, o chat fica na frente da barra de preço */
    z-index: 110;
    width: 100%;
    max-width: 100%;
    height: 88vh;
    max-height: 88vh;
    border-radius: 16px 16px 0 0;

    body.has-price-summary-bar & {
      bottom: 0;
      max-height: 88vh;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${(p) => p.theme.colors.primary};
  color: ${(p) => p.theme.colors.white};
`;

const Avatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  flex-shrink: 0;
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;

  strong {
    display: block;
    font-size: 16px;
    font-weight: 700;
  }
  span {
    font-size: 13px;
    opacity: 0.85;
  }
`;

const IconButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${(p) => p.theme.colors.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { background: rgba(255, 255, 255, 0.15); }
  &:focus-visible {
    outline: 3px solid ${(p) => p.theme.colors.white};
    outline-offset: -2px;
  }
`;

const Composer = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid ${(p) => p.theme.colors.border};
  background: ${(p) => p.theme.colors.white};
`;

const TextArea = styled.textarea`
  flex: 1;
  resize: none;
  max-height: 120px;
  min-height: 46px;
  padding: 12px 14px;
  border: 2px solid ${(p) => p.theme.colors.border};
  border-radius: 10px;
  font-family: ${(p) => p.theme.fonts.primary};
  font-size: 16px; /* evita zoom no iOS e é legível */
  line-height: 1.4;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.colors.secondary};
  }
`;

const SendButton = styled.button`
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  border: none;
  border-radius: 10px;
  background: ${(p) => p.theme.colors.secondary};
  color: ${(p) => p.theme.colors.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) { background: ${(p) => p.theme.colors.hoverSecondary}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:focus-visible {
    outline: 3px solid ${(p) => p.theme.colors.info};
    outline-offset: 2px;
  }
`;

const AleWindow = ({ onClose }) => {
  const { messages, isSending, sendMessage, sendQuickReply } = useAleChat();
  const [text, setText] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!text.trim() || isSending) return;
    sendMessage(text);
    setText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <Panel role="dialog" aria-label="Assistente virtual Ale">
      <Header>
        <Avatar aria-hidden="true">A</Avatar>
        <HeaderText>
          <strong>{ALE_TEXTS.headerTitle}</strong>
          <span>{ALE_TEXTS.headerSubtitle}</span>
        </HeaderText>
        <IconButton type="button" onClick={onClose} aria-label="Fechar conversa">
          <LuX size={22} aria-hidden="true" />
        </IconButton>
      </Header>

      <AleMessageList
        messages={messages}
        isSending={isSending}
        onQuickReply={sendQuickReply}
      />

      <Composer onSubmit={handleSubmit}>
        <TextArea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, ALE_CONFIG.maxMessageLength))}
          onKeyDown={handleKeyDown}
          placeholder={ALE_TEXTS.inputPlaceholder}
          aria-label="Escreva sua mensagem para o Ale"
          rows={1}
          disabled={isSending}
        />
        <SendButton type="submit" disabled={!text.trim() || isSending} aria-label="Enviar mensagem">
          <LuSend size={20} aria-hidden="true" />
        </SendButton>
      </Composer>
    </Panel>
  );
};

export default AleWindow;
